// Cycle-16 composer multi-line text-input state. Renders the composer
// text field that grows with content up to a cap (default 5 lines).
// Pure controlled component — host owns text state.

import React from 'react';
import { StyleSheet, type TextStyle, TextInput, View } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { tokens } from '../../design-system/tokens';

export interface ComposerMultilineStateProps {
  text: string;
  onChangeText: (next: string) => void;
  placeholder: string;
  maxLines?: number | undefined;
  testID?: string | undefined;
}

const LINE_HEIGHT = tokens.typography.fontSize.body * tokens.typography.lineHeight.normal;

export const ComposerMultilineState = React.memo<ComposerMultilineStateProps>(
  ({ text, onChangeText, placeholder, maxLines, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    const cap = Math.max(1, maxLines ?? 5);
    const maxHeight = LINE_HEIGHT * cap + tokens.spacing.sm * 2;

    const inputStyle: TextStyle = {
      color: colors.text.primary,
      fontFamily: tokens.typography.fontFamily.base,
      fontSize: tokens.typography.fontSize.body,
      lineHeight: LINE_HEIGHT,
      maxHeight,
    };

    return (
      <View style={styles.container} testID={testID}>
        <TextInput
          accessibilityLabel="Message"
          style={[styles.input, inputStyle]}
          value={text}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={colors.text.tertiary}
          multiline
          textAlignVertical="top"
        />
      </View>
    );
  },
);

ComposerMultilineState.displayName = 'ComposerMultilineState';

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
  },
  input: {
    minHeight: LINE_HEIGHT,
    padding: 0,
  },
});
